import { UserHairBuns } from "@/icons/UserHairBuns.tsx";
import {
  photoMenHairStylesMap,
  photoMenHairStylesPhotoMap,
  photoWomenHairStylesMap,
  photoWomenHairStylesPhotoMap,
} from "@/lib/helpers.ts";
import { LoraModel } from "@/models/lora.model.ts";
import classes from "@/ui/shared/StyleSelect.module.css";
import { useMemo, useCallback, memo, useState, useEffect } from "react";
import { useDisclosure } from "@mantine/hooks";
import {
  Accordion,
  Button,
  CloseButton,
  Image,
  Modal,
  Paper,
  Text,
  TextInput,
} from "@mantine/core";
import { AnglesUpDown } from "@/icons/AnglesUpDown";

interface Props {
  value: string | "";
  setValue: (hair: string | "") => void;
  lora: LoraModel | undefined;
}

export const HairSelect = memo(function HairSelect({
  value,
  setValue,
  lora,
}: Props) {
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedTab, setSelectedTab] = useState<string | null>("men");

  useEffect(() => {
    if (lora) {
      setSelectedTab(lora.gender === "male" ? "men" : "women");
    }
  }, [lora]);

  const handleClose = useCallback(() => {
    close();
  }, [close]);

  const modalStyles = useMemo(() => ({ body: { paddingBottom: 0 } }), []);

  const getHairStyle = (hair: string) => {
    if (photoMenHairStylesMap.hasOwnProperty(hair)) {
      return photoMenHairStylesMap[hair as keyof typeof photoMenHairStylesMap];
    }

    if (photoWomenHairStylesMap.hasOwnProperty(hair)) {
      return photoWomenHairStylesMap[
        hair as keyof typeof photoWomenHairStylesMap
      ];
    }

    return "";
  };

  const getImage = (hair: string) => {
    if (photoMenHairStylesPhotoMap.hasOwnProperty(hair)) {
      return photoMenHairStylesPhotoMap[
        hair as keyof typeof photoMenHairStylesPhotoMap
      ];
    }

    if (photoWomenHairStylesPhotoMap.hasOwnProperty(hair)) {
      return photoWomenHairStylesPhotoMap[
        hair as keyof typeof photoWomenHairStylesPhotoMap
      ];
    }

    return "";
  };

  return (
    <>
      <TextInput
        styles={{ input: { cursor: "pointer" } }}
        label="Hairstyle"
        onClick={open}
        size="md"
        value={getHairStyle(value)}
        leftSection={
          value ? (
            <Image src={getImage(value)} radius="sm" w={30} h={30} />
          ) : (
            <UserHairBuns />
          )
        }
        rightSection={
          value ? (
            <CloseButton onClick={() => setValue("")} />
          ) : (
            <AnglesUpDown />
          )
        }
        readOnly
      />

      <Modal
        styles={modalStyles}
        fullScreen
        opened={opened}
        onClose={close}
        title="Hairstyles"
        className="relative"
      >
        <Accordion
          variant="contained"
          value={selectedTab}
          onChange={setSelectedTab}
        >
          <Accordion.Item value="men">
            <Accordion.Control>
              <Text>Men</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <div className="flex flex-wrap justify-center md:justify-start gap-3">
                <div className="flex flex-wrap gap-2 items-center justify-center">
                  {Object.keys(photoMenHairStylesMap).map((key) => (
                    <div
                      key={key}
                      onClick={() => {
                        setValue(key);
                        close();
                      }}
                      className="flex flex-col items-center p-1 relative cursor-pointer text-sm"
                    >
                      {key === value && <div className={classes.indicator} />}
                      <Image
                        className="z-[2]"
                        src={
                          photoMenHairStylesPhotoMap[
                            key as keyof typeof photoMenHairStylesPhotoMap
                          ]
                        }
                        radius="sm"
                        w={94}
                        h={94}
                      />
                      {key === value && (
                        <span className={classes.selected}>
                          {
                            photoMenHairStylesMap[
                              key as keyof typeof photoMenHairStylesMap
                            ]
                          }
                        </span>
                      )}
                      {key !== value && (
                        <span className={classes.deselected}>
                          {
                            photoMenHairStylesMap[
                              key as keyof typeof photoMenHairStylesMap
                            ]
                          }
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </Accordion.Panel>
          </Accordion.Item>
          <Accordion.Item value="women">
            <Accordion.Control>
              <Text>Women</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <div className="flex flex-wrap justify-center md:justify-start gap-3">
                <div className="flex flex-wrap gap-2 items-center justify-center">
                  {Object.keys(photoWomenHairStylesMap).map((key) => (
                    <div
                      key={key}
                      onClick={() => {
                        setValue(key);
                        close();
                      }}
                      className="flex flex-col items-center p-1 relative cursor-pointer text-sm"
                    >
                      {key === value && <div className={classes.indicator} />}
                      <Image
                        className="z-[2]"
                        src={
                          photoWomenHairStylesPhotoMap[
                            key as keyof typeof photoWomenHairStylesPhotoMap
                          ]
                        }
                        radius="sm"
                        w={94}
                        h={94}
                      />
                      {key === value && (
                        <span className={classes.selected}>
                          {
                            photoWomenHairStylesMap[
                              key as keyof typeof photoWomenHairStylesMap
                            ]
                          }
                        </span>
                      )}
                      {key !== value && (
                        <span className={classes.deselected}>
                          {
                            photoWomenHairStylesMap[
                              key as keyof typeof photoWomenHairStylesMap
                            ]
                          }
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>

        <Paper p="lg" radius={0} className="sticky bottom-0 mt-5 z-30">
          <div className="flex justify-center">
            <Button
              onClick={handleClose}
              variant="default"
              className="flex items-center gap-2 bg-transparent p-1 text-xs"
            >
              Close
            </Button>
          </div>
        </Paper>
      </Modal>
    </>
  );
});
